import { authorizedFetch } from 'utils/fetchUtils';
import { API_ROUTES, getReactAppApiUrl } from 'utils/constants';
import type {
  DonationConfirmRequest,
  DonationConfirmResponse,
  DonationCreateRequest,
  DonationCreateResponse,
  DonationStatusResponse,
} from 'types/donation';

function buildApiUrl(endpoint: string): string {
  const base = getReactAppApiUrl().replace(/\/+$/, '');
  const path = endpoint.replace(/^\/+/, '');
  return `${base}/${path}`;
}

async function parseJsonOrThrow<T>(
  res: Response,
  fallback: string,
): Promise<T> {
  if (!res.ok) {
    let message = fallback;
    try {
      const body = (await res.json()) as { detail?: string; message?: string };
      if (typeof body.detail === 'string' && body.detail.trim()) {
        message = body.detail;
      } else if (typeof body.message === 'string' && body.message.trim()) {
        message = body.message;
      }
    } catch {
      message = res.statusText || fallback;
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export async function createDonation(
  payload: DonationCreateRequest,
): Promise<DonationCreateResponse> {
  const url = buildApiUrl(API_ROUTES.DONATIONS.CREATE);
  const res = await authorizedFetch(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    { endpoint: API_ROUTES.DONATIONS.CREATE, skipAuth: true },
  );
  return parseJsonOrThrow<DonationCreateResponse>(
    res,
    'Failed to create donation.',
  );
}

export async function confirmDonation(
  payload: DonationConfirmRequest,
): Promise<DonationConfirmResponse> {
  const url = buildApiUrl(API_ROUTES.DONATIONS.CONFIRM);
  const res = await authorizedFetch(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    { endpoint: API_ROUTES.DONATIONS.CONFIRM, skipAuth: true },
  );
  return parseJsonOrThrow<DonationConfirmResponse>(
    res,
    'Failed to confirm donation.',
  );
}

export async function getDonationStatus(
  donationId: string,
): Promise<DonationStatusResponse> {
  const endpoint = API_ROUTES.DONATIONS.STATUS.replace(
    ':id',
    encodeURIComponent(donationId),
  );
  const url = buildApiUrl(endpoint);
  const res = await authorizedFetch(
    url,
    { method: 'GET' },
    { endpoint, skipAuth: true },
  );
  return parseJsonOrThrow<DonationStatusResponse>(
    res,
    'Failed to fetch donation status.',
  );
}
